import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { clearAuthDataAction, setDataAndTokenAction } from '@/redux/slicers/auth.slice';
import { Messages } from '@/constants/message';
import { useRootSelector } from './selector.hook';
import {
  setToken,
  setRefreshToken,
  removeToken,
  removeRefreshToken,
  getToken,
  getRefreshToken,
  isTokenExpired,
  isRefreshTokenExpired,
} from '@/utils/common';
import { useApi, useCaller } from '@/hooks/api.hook';

import { refreshAccessToken, waitForTokenRefresh, isTokenRefreshing } from '@/utils/axios';

// Global refresh function để đảm bảo chỉ gọi 1 lần
const globalRefreshToken = async () => {
  try {
    const newAccessToken = await refreshAccessToken();

    if (newAccessToken) {
      const newRefreshToken = getRefreshToken();

      return {
        success: true,
        accessToken: newAccessToken,
        refreshToken: newRefreshToken || '',
      };
    }

    throw new Error('Refresh failed');
  } catch (error) {
    console.error('❌ Global refresh error:', error);
    throw error;
  }
};

export const useAuth = () => {
  const dispatch = useDispatch();
  const api = useApi('');
  const caller = useCaller();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useRootSelector((state) => state.auth);

  const clearData = useCallback(() => {
    removeToken();
    removeRefreshToken();
    dispatch(clearAuthDataAction());
    navigate('/login');
  }, [dispatch, navigate]);

  const login = useCallback(
    async (email: string, password: string) => {
      const response = await caller(
        () =>
          api.post(`/v1/api/authenticate`, {
            email,
            password,
          }),
        {
          loadingKey: 'login-loading',
          successMessage: Messages.LOGIN_SUCCESS,
        },
      );

      // useCaller returns null after it has surfaced the API error message.
      // Do not destructure a failed response or replace a useful message such as
      // "Invalid credentials" with a generic system error.
      if (!response) {
        return;
      }

      const { content, succeeded } = response;

      if (succeeded) {
        let token = content?.accessToken;
        let refreshToken = content?.refreshToken;

        // Lưu token ban đầu vào localStorage
        setToken(token);
        setRefreshToken(refreshToken);

        // Check xem token có hết hạn không
        if (token && isTokenExpired(token)) {
          // Token đã hết hạn -> cần refresh trước khi call profile
          if (refreshToken && !isRefreshTokenExpired(refreshToken)) {
            try {
              // Call refresh API
              const newAccessToken = await refreshAccessToken();

              if (newAccessToken) {
                // Refresh thành công -> lưu token mới vào local (thay thế cũ)
                token = newAccessToken;
                const newRefreshToken = getRefreshToken(); // Lấy refresh token mới từ localStorage (đã được set trong refreshAccessToken)

                // Đảm bảo token mới đã được lưu vào localStorage
                setToken(token);
                if (newRefreshToken) {
                  setRefreshToken(newRefreshToken);
                  refreshToken = newRefreshToken;
                }

                // Update Redux với token mới
                dispatch(
                  setDataAndTokenAction({
                    token,
                    refreshToken,
                  }),
                );
              } else {
                // Refresh thất bại -> đá về trang login
                clearData();
                return;
              }
            } catch (error) {
              // Refresh thất bại -> đá về trang login
              console.error('Token refresh failed after login:', error);
              clearData();
              return;
            }
          } else {
            // Refresh token không hợp lệ -> đá về trang login
            clearData();
            return;
          }
        } else {
          // Token còn hạn -> update Redux với token hiện tại
          dispatch(
            setDataAndTokenAction({
              token,
              refreshToken,
            }),
          );
        }

        // Sau khi đảm bảo token hợp lệ, mới call profile
        // Đảm bảo token đã được lưu vào localStorage trước khi call profile
        // Đọc lại token từ localStorage để verify
        const savedToken = getToken();
        if (savedToken) {
          // Token đã được lưu -> call profile
          await fetchProfile();
        } else {
          // Token chưa được lưu -> đợi một chút rồi thử lại
          await new Promise((resolve) => setTimeout(resolve, 100));
          const retryToken = getToken();
          if (retryToken) {
            await fetchProfile();
          } else {
            console.error('Token not saved after login');
            clearData();
          }
        }
      }
    },
    [api, caller],
  );

  // DEPRECATED: refreshToken function removed
  // Axios interceptor now handles all token refresh automatically
  // This prevents duplicate refresh calls and race conditions

  const checkAuthStatus = useCallback(async () => {
    try {
      const token = getToken();
      const refreshToken = getRefreshToken();

      // Nếu không có token, user chưa đăng nhập
      if (!token) {
        dispatch(clearAuthDataAction());
        return false;
      }

      // Nếu token còn hạn, user đã đăng nhập
      if (!isTokenExpired(token)) {
        dispatch(
          setDataAndTokenAction({
            isAuthenticated: true,
            token,
            refreshToken,
          }),
        );
        return true;
      }

      // Token hết hạn - kiểm tra refresh token
      // KHÔNG refresh ngay ở đây, để axios interceptor tự động xử lý khi gặp 401
      if (!refreshToken || isRefreshTokenExpired(refreshToken)) {
        clearData();
        return false;
      }

      // Refresh token còn hạn - chỉ set state, KHÔNG gọi refresh API
      // Axios interceptor sẽ tự động refresh khi API call thực sự bị 401
      dispatch(
        setDataAndTokenAction({
          isAuthenticated: true,
          token, // Token cũ (đã hết hạn) nhưng vẫn set để axios interceptor xử lý
          refreshToken,
        }),
      );

      return true;
    } catch (error) {
      console.error('Auth check error:', error);
      clearData();
      return false;
    }
  }, [clearData, dispatch]);

  const logout = useCallback(() => {
    clearData();
  }, [clearData]);

  const fetchProfile = useCallback(async () => {
    const token = getToken();

    if (token && isTokenExpired(token) && isTokenRefreshing()) {
      const newToken = await waitForTokenRefresh();
      if (!newToken) {
        // Refresh thất bại, không call profile
        console.warn('Token refresh failed, skipping profile call');
        return;
      }
    }

    if (token && isTokenExpired(token) && !isTokenRefreshing()) {
      // Đợi một chút để axios interceptor có thể bắt 401 và refresh
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check lại xem có đang refresh không
      if (isTokenRefreshing()) {
        const newToken = await waitForTokenRefresh();
        if (!newToken) {
          console.warn('Token refresh failed, skipping profile call');
          return;
        }
      }
    }

    const result = await caller(() => api.get('/v1/api/user/profile'), {
      loadingKey: 'profile-loading',
    });
    if (result?.succeeded) {
      dispatch(
        setDataAndTokenAction({
          user: result.content,
        }),
      );
    }
  }, [api, caller]);

  return {
    login,
    logout,
    clearData,
    checkAuthStatus,
    isAuthenticated,
    isLoading,
    fetchProfile,
  };
};

// Export global refresh function để sử dụng ở nơi khác
export { globalRefreshToken };
